import { Button } from "@babackapp/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@babackapp/ui/components/dialog";
import { Textarea } from "@babackapp/ui/components/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@babackapp/ui/components/tooltip";
import { Loader2, StickyNote } from "lucide-react";
import { useState } from "react";

export type LegacyPortalReasonMode = "switch-off" | "edit";

const DIALOG_COPY = {
  "switch-off": {
    title: "Repasser sur l'ancien portail",
    description:
      "Indique pourquoi ce shop repasse sur l'ancien portail. Le commentaire reste visible à côté du switch.",
    submit: "Repasser sur l'ancien portail",
  },
  edit: {
    title: "Pourquoi l'ancien portail",
    description: "Raison pour laquelle ce shop est sur l'ancien portail.",
    submit: "Enregistrer",
  },
} as const satisfies Record<
  LegacyPortalReasonMode,
  { title: string; description: string; submit: string }
>;

/**
 * The reason is required in both modes: switching off without one is the whole
 * point of the dialog, and clearing it from the edit mode would drop the only
 * trace of why the shop left the V3 portal.
 */
function LegacyReasonDialog({
  mode,
  initialReason,
  isPending,
  onSubmit,
  onClose,
}: {
  mode: LegacyPortalReasonMode | null;
  initialReason: string;
  isPending: boolean;
  onSubmit: (reason: string) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState(initialReason);
  const copy = mode ? DIALOG_COPY[mode] : null;

  // A draft dropped by cancelling must not come back on the next opening.
  const close = () => {
    onClose();
    setReason(initialReason);
  };

  return (
    <Dialog open={mode !== null} onOpenChange={(open) => !open && close()}>
      {copy && (
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{copy.title}</DialogTitle>
            <DialogDescription>{copy.description}</DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (reason.trim()) onSubmit(reason.trim());
            }}
          >
            <Textarea
              autoFocus
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={1000}
              rows={4}
              placeholder="Ex. : CSS personnalisé incompatible, en attente du marchand…"
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={close}>
                Annuler
              </Button>
              <Button type="submit" disabled={!reason.trim() || isPending}>
                {isPending && <Loader2 className="size-4 animate-spin" />}
                {copy.submit}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      )}
    </Dialog>
  );
}

/** Opens the reason in edit mode; its text shows as a tooltip on hover. */
function LegacyReasonNote({
  reason,
  onClick,
}: {
  reason: string;
  onClick: () => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          aria-label="Modifier la raison du retour à l'ancien portail"
          className="text-amber-500 transition-colors hover:text-amber-600"
        >
          <StickyNote className="size-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs whitespace-pre-wrap">
        {reason}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Everything a V3 portal switch needs around it: the note shown while the shop
 * is on the legacy widget, and the dialog asking why when it is switched off.
 * The caller owns `mode` so its switch can open the dialog instead of saving.
 */
export function LegacyPortalReason({
  isEnabled,
  reason,
  mode,
  onModeChange,
  isPending,
  onSubmit,
}: {
  isEnabled: boolean;
  reason: string | null;
  mode: LegacyPortalReasonMode | null;
  onModeChange: (mode: LegacyPortalReasonMode | null) => void;
  isPending: boolean;
  onSubmit: (reason: string, mode: LegacyPortalReasonMode) => void;
}) {
  return (
    <>
      {!isEnabled && reason && (
        <LegacyReasonNote
          reason={reason}
          onClick={() => onModeChange("edit")}
        />
      )}
      <LegacyReasonDialog
        // Remounts once the saved reason comes back, dropping the draft.
        key={reason ?? ""}
        mode={mode}
        initialReason={reason ?? ""}
        isPending={isPending}
        onSubmit={(next) => mode && onSubmit(next, mode)}
        onClose={() => onModeChange(null)}
      />
    </>
  );
}
